import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClientes } from '@/features/clientes/hooks/useClientes';
import { ClienteFormModal } from '@/features/clientes/pages/ClienteFormModal';
import type { ClienteResumo } from '@/features/clientes/api/clientesApi';
import { BadgeSyncPendente } from '@/shared/ui/BadgeSyncPendente';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Paginacao } from '@/shared/ui/Paginacao';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { IconEdit, IconRefresh, IconTrash, IconUsers } from '@/shared/ui/Icon';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';
import { useRotaEntidade } from '@/app/useRotaEntidade';
import { ROTAS } from '@/app/rotas';

type ModoForm =
  | { tipo: 'fechado' }
  | { tipo: 'novo' }
  | { tipo: 'editar'; cliente: ClienteResumo };

export function ClientesPage() {
  const { clientes, carregando, erro, recarregar, atualizar, excluir } = useClientes();
  // Criar e editar têm endereço próprio (`/clientes/novo`,
  // `/clientes/:id/editar`): dá para chegar por link e o voltar do
  // navegador fecha o formulário.
  const rota = useRotaEntidade(ROTAS.clientes);
  // `?secao=veiculos` (vindo do contador de veículos) abre a edição já
  // rolada na lista de veículos do cliente.
  const [parametros] = useSearchParams();
  const focarVeiculos = parametros.get('secao') === 'veiculos';
  const [busca, setBusca] = useState('');
  const dialogos = useDialogos();

  const modo = useMemo<ModoForm>(() => {
    if (rota.modo === 'novo') return { tipo: 'novo' };
    if (rota.modo === 'editar') {
      const cliente = clientes.find((c) => c.id === rota.id);
      // Enquanto a lista carrega (ou se o id não existe mais) a tela
      // segue como listagem — nada de modal preso em branco.
      return cliente ? { tipo: 'editar', cliente } : { tipo: 'fechado' };
    }
    return { tipo: 'fechado' };
  }, [rota, clientes]);

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (q === '') return clientes;
    return clientes.filter((c) => {
      const alvo = normalizarBusca(
        `${c.nome} ${c.documento ?? ''} ${c.email ?? ''} ${c.telefone ?? ''}`,
      );
      return alvo.includes(q);
    });
  }, [clientes, busca]);

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(filtrados, 25);

  async function confirmarExclusao(cliente: ClienteResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir cliente',
      mensagem: `Excluir o cliente "${cliente.nome}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await excluir(cliente.id);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Clientes"
        descricao="Empresas e pessoas que recebem ordens de serviço."
        acoes={
          <>
            <Button onClick={() => void recarregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => rota.abrir('novo')}>
              + Novo cliente
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">
            {filtrados.length} de {clientes.length} cliente(s)
          </span>
          <SearchInput
            valor={busca}
            onChange={setBusca}
            placeholder="Buscar por nome, documento, e-mail ou telefone…"
            ariaLabel="Buscar cliente"
          />
        </div>
        <div className="card__body card__body--flush">
          {clientes.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconUsers />}
              titulo="Nenhum cliente cadastrado ainda."
              descricao="Os clientes aparecerão aqui assim que forem cadastrados."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconUsers />}
              titulo="Nenhum cliente corresponde à busca."
              descricao="Tente outro termo ou limpe o campo de busca."
            />
          ) : (
            <div className="table-scroll"><table className="table table--responsive">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Documento</th>
                  <th>E-mail</th>
                  <th>Telefone</th>
                  <th className="table__numeric">Veículos</th>
                  <th style={{ width: 1 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginados.map((c) => (
                  <tr key={c.id}>
                    <td className="table__primary">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {c.nome}
                        <BadgeSyncPendente pendente={c.syncPendente} />
                      </span>
                    </td>
                    <td className="table__muted" data-label="Documento">{c.documento}</td>
                    <td className="table__muted" data-label="E-mail">{c.email ?? '—'}</td>
                    <td className="table__muted" data-label="Telefone">{c.telefone ?? '—'}</td>
                    <td className="table__numeric" data-label="Veículos">
                      <ContadorVeiculos
                        cliente={c}
                        aoAbrir={() => rota.abrir(`${c.id}/editar?secao=veiculos`)}
                      />
                    </td>
                    <td>
                      <div className="row-actions">
                        <Button
                          className="btn--icon"
                          aria-label={`Editar ${c.nome}`}
                          onClick={() => rota.abrir(`${c.id}/editar`)}
                        >
                          <IconEdit />
                        </Button>
                        <Button
                          className="btn--icon btn--danger"
                          aria-label={`Excluir ${c.nome}`}
                          onClick={() => void confirmarExclusao(c)}
                        >
                          <IconTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            tamanhoPagina={tamanhoPagina}
            onMudarPagina={setPagina}
          />
        </div>
      </div>

      {modo.tipo !== 'fechado' && (
        <ClienteFormModal
          modo={modo}
          focarVeiculos={focarVeiculos}
          aoFechar={rota.fechar}
          aoSalvar={async (dados) => {
            if (modo.tipo === 'editar') {
              await atualizar(modo.cliente.id, dados);
            }
            rota.fechar();
            await recarregar();
          }}
        />
      )}
    </section>
  );
}

/**
 * Quantidade de veículos do cliente. Com veículos, vira um botão que abre
 * o cadastro do cliente já na seção "Veículos" — é lá que os veículos
 * moram (não há tela própria de veículos). Sem veículos, só um traço.
 */
function ContadorVeiculos({
  cliente,
  aoAbrir,
}: {
  cliente: ClienteResumo;
  aoAbrir: () => void;
}) {
  const n = cliente.quantidadeVeiculos ?? 0;
  if (n === 0) {
    return (
      <span className="table__muted" aria-label={`${cliente.nome} não tem veículos`}>
        —
      </span>
    );
  }
  const rotulo = `Ver ${n === 1 ? '1 veículo' : `${n} veículos`} de ${cliente.nome}`;
  return (
    <button
      type="button"
      className="contador-veiculos"
      onClick={aoAbrir}
      aria-label={rotulo}
      title={rotulo}
    >
      <span className="contador-veiculos__pilula">{n}</span>
    </button>
  );
}
